import { AutopilotTaskDto, TaskCategoryTaskType, useToggleTaskSteps } from '@urbanx/agx.activities.client'
import { AuthContext } from '@urbanx/agx.auth.client'
import {
    DocumentTypes,
    UploadedDocument,
} from '@urbanx/agx.campaigns.client'
import { AxiosProgressEvent, GenericAbortSignal } from 'axios'
import { useCallback, useContext, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { uploadFile } from 'services'
interface UseMarkedAsExchangeProps {
    task: AutopilotTaskDto
}

export const useMarkedAsExchange = ({ task }: UseMarkedAsExchangeProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedOption, setSelectedOption] = useState<string | undefined>()
  const [urbanXisNoted, setUrbanXisNoted] = useState(false)
  const [exchangeLetterFiles, setExchangeLetterFiles] = useState<
    (UploadedDocument | File)[]
  >([])
  const [signedContractFiles, setSignedContractFiles] = useState<
    (UploadedDocument | File)[]
  >([])
  const [isSubmitting, setIsSubmitting] = useState(false)

    const { agencyId, contractId } = useParams()
    const { getAuthToken } = useContext(AuthContext)
    const isSolicitorExchange =
        task.category?.taskType === TaskCategoryTaskType.SolicitorExchangeInformation

  const { toggleTaskSteps } = useToggleTaskSteps({
    onSuccess: () => {},
    onError: () => {},
  })

  const openModal = () => {
    setIsOpen(true)
  }
  const closeModal = () => {
    setIsOpen(false)
  }

  const resetState = useCallback(() => {
    setSelectedOption(undefined)
    setUrbanXisNoted(false)
    setExchangeLetterFiles([])
    setSignedContractFiles([])
    setIsSubmitting(false)
  }, [])

  const buildRenamedFormData = useCallback(
    (formData: FormData) => {
      const file = formData.get('File')
      if (!file || typeof File === 'undefined' || !(file instanceof File)) {
        return formData
      }

      const renamedFile = new File([file], `${contractId}/${file.name}`, {
        type: file.type,
        lastModified: file.lastModified,
      })
      const renamedFormData = new FormData()
      renamedFormData.append('File', renamedFile)
      return renamedFormData
    },
    [contractId]
  )

  const onUploadFile = useCallback(
    async (
      authToken: string,
      file: string | FormData,
      uploadCampaignId: string,
      uploadedFormType: DocumentTypes,
      uploadProgress?: (progressEvent: AxiosProgressEvent) => void,
      abortSignal?: GenericAbortSignal,
      uploadContractId?: string
    ) => {
      try {
        const renamedFormData =
          typeof FormData !== 'undefined' && file instanceof FormData
            ? buildRenamedFormData(file)
            : file

        return await uploadFile(
          authToken,
          renamedFormData,
          uploadCampaignId,
          uploadedFormType,
          uploadProgress ?? null,
          abortSignal ?? null,
          undefined,
          uploadContractId
        )
      } catch {
        return undefined
      }
    },
    [buildRenamedFormData]
  )

  useEffect(() => {
    if (!isOpen) {
      setSelectedOption(undefined)
      setUrbanXisNoted(false)
      resetState()
    }
  }, [isOpen, resetState])

  const onExchangeLetterChanged = useCallback(
    ({ value }: { value?: (UploadedDocument | File)[] }) => {
      setExchangeLetterFiles(value ?? [])
    },
    []
  )

  const onSignedContractChanged = useCallback(
    ({ value }: { value?: (UploadedDocument | File)[] }) => {
      setSignedContractFiles(value ?? [])
    },
    []
  )

  const uploadSelectedFile = useCallback(
    async (
      authToken: string,
      uploadCampaignId: string,
      documentType: DocumentTypes,
      selectedFile?: UploadedDocument | File,
      uploadContractId?: string
    ) => {
      if (!selectedFile || !(selectedFile instanceof File)) return selectedFile

      const formData = new FormData()
      formData.append('File', selectedFile)

      return onUploadFile(
        authToken,
        formData,
        uploadCampaignId,
        documentType,
        undefined,
        undefined,
        uploadContractId
      )
    },
    [onUploadFile]
  )

  const onConfirm = useCallback(
    async (campaignId: string) => {
      if (!campaignId || exchangeLetterFiles.length === 0) return
      if (
        isSolicitorExchange &&
        selectedOption === 'Yes' &&
        signedContractFiles.length === 0
      ) {
        return
      }

      const authToken = await getAuthToken()
      if (!authToken) return

      setIsSubmitting(true)

      try {
        await uploadSelectedFile(
          authToken,
          campaignId,
          DocumentTypes.ExchangeLetter,
          exchangeLetterFiles[0],
          contractId
        )

        if (isSolicitorExchange && selectedOption === 'Yes') {
          await uploadSelectedFile(
            authToken,
            campaignId,
            DocumentTypes.SignedContract,
            signedContractFiles[0],
            contractId
          )

          toggleTaskSteps({
            campaignId,
            contractId,
            category: task.category,
          })
        }

        closeModal()
      } finally {
        setIsSubmitting(false)
      }
    },
    [
      closeModal,
      exchangeLetterFiles,
      getAuthToken,
      isSolicitorExchange,
      selectedOption,
      signedContractFiles,
      uploadSelectedFile,
    ]
  )

  const isConfirmDisabled =
    (isSolicitorExchange && !selectedOption) ||
    exchangeLetterFiles.length === 0 ||
    (isSolicitorExchange &&
      selectedOption === 'Yes' &&
      (signedContractFiles.length === 0 || !urbanXisNoted)) ||
    isSubmitting

  return {
    agencyId: agencyId || '',
    exchangeLetterFiles,
    getAuthToken,
    isConfirmDisabled,
    isOpen,
    openModal,
    closeModal,
    selectedOption,
    isSolicitorExchange,
    setIsDialogOpen: setIsOpen,
    setSelectedOption,
    setUrbanXisNoted,
    urbanXisNoted,
    onConfirm,
    onExchangeLetterChanged,
    onSignedContractChanged,
    signedContractFiles,
    onUploadFile,
  }
}
